/**
 * `/admin/permissions` — Task T2-D (Tier-2 ops, pillars-deepening program).
 *
 * The permission-grants API has existed since Task 15 with **zero web callers**:
 * nobody could make someone a recruiter, an HR admin or a payroll approver from the
 * UI before this screen. `nav/menu.ts` gates it on `workforce.manage_policies` alone
 * — the identical single requirement `policies` uses, never a role check — and the
 * route guard (`App.tsx`) refuses the whole screen server-side-honestly before any
 * of the three requests below fires for a session that lacks it.
 *
 * ── Floors are prose, never fake rows ────────────────────────────────────────
 *
 * `src/lib/permissions.ts`'s `DEFAULT_GRANTS` gives every role an implicit floor —
 * `ceo` holds `workforce.view_workspace` plus the five `manage_*` codes with no
 * `wp_permission_grants` row behind them at all. This table holds only the
 * **deviations** from that floor, and the header below says so in words rather than
 * synthesising a floor as a grant row: a row this screen invented would be
 * indistinguishable, in the table, from one an administrator actually wrote and
 * `wp_audit_log` actually recorded.
 *
 * ── Client-side filtering, deliberately ──────────────────────────────────────
 *
 * `listPermissionGrants` is `x-wp-pagination: none` and company-wide — "what has
 * been granted here" would be a lie if it hid a page — so the four filters below
 * narrow the one response already in memory rather than becoming server
 * parameters, which is the opposite of the rule every paged screen in this
 * codebase keeps (`paths.ts`'s own WP-L5 block) for the reason that rule states:
 * on an unpaginated, complete list there is no "…among the first fifty" for a
 * client-side filter to lie about.
 *
 * ── Created vs. replaced, without an HTTP status ─────────────────────────────
 *
 * `useApiMutation`'s `MutationResult` carries no raw status code, so `201` vs `200`
 * — "Đã cấp quyền" vs "Đã cập nhật quyền" — is told apart by `existingGrantFor`
 * (`permissions/grants.ts`) against the list **as it stood the moment submit was
 * pressed**, captured in `preSubmitGrants`. Checked before the reload that follows
 * every successful write, never after — after, the grant always exists.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { Translate } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import {
  employeeDepartmentsPath,
  employeePath,
  type EmployeeDepartmentFacetList,
  type EmployeeProfile,
} from '../../api/employees';
import {
  PERMISSION_EFFECTS,
  PERMISSION_GRANTS_PATH,
  PERMISSION_SUBJECT_TYPES,
  permissionGrantPath,
  type PermissionEffect,
  type PermissionGrant,
  type PermissionGrantList,
  type PermissionGrantRequest,
  type PermissionSubjectType,
} from '../../api/permission-grants';
import {
  EFFECT_PRESENTATION,
  existingGrantFor,
  GRANT_STATUS_PRESENTATION,
  GRANT_STATUSES,
  grantStatusOf,
  PERMISSION_GROUP_LABEL_KEY,
  PERMISSION_GROUPS,
  permissionGroupOf,
  ROLE_SUBJECT_LABEL_KEY,
  SUBJECT_TYPE_LABEL_KEY,
  type GrantRoleSubject,
  type GrantStatus,
  type PermissionGroup,
} from '../../permissions/grants';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { GrantForm } from './permissions/GrantForm';
import { RevokeGrantPanel } from './permissions/RevokeGrantPanel';

export interface PermissionGrantsScreenProps {
  /** Injected for a deterministic "active vs. expired" boundary in tests. */
  readonly nowMs?: number;
}

/** The subject's own type label plus its resolved value — shared by the table and the revoke dialog. */
function subjectValueText(
  t: Translate,
  grant: Pick<PermissionGrant, 'subject_type' | 'subject_key'>,
  departmentNameById: ReadonlyMap<string, string>,
  resolvedEmployeeName: string | null,
): string {
  if (grant.subject_type === 'employee') return resolvedEmployeeName ?? grant.subject_key;
  if (grant.subject_type === 'role') {
    const roleKey = (
      ROLE_SUBJECT_LABEL_KEY as Partial<
        Record<string, (typeof ROLE_SUBJECT_LABEL_KEY)[GrantRoleSubject]>
      >
    )[grant.subject_key];
    return roleKey === undefined ? grant.subject_key : t(roleKey);
  }
  if (grant.subject_type === 'department') {
    return departmentNameById.get(grant.subject_key) ?? grant.subject_key;
  }
  return grant.subject_key;
}

function subjectLabelText(
  t: Translate,
  grant: Pick<PermissionGrant, 'subject_type' | 'subject_key'>,
  departmentNameById: ReadonlyMap<string, string>,
  resolvedEmployeeName: string | null,
): string {
  return `${t(SUBJECT_TYPE_LABEL_KEY[grant.subject_type])}: ${subjectValueText(t, grant, departmentNameById, resolvedEmployeeName)}`;
}

/**
 * One row's "Đối tượng" cell. `GET /employees/{id}` is read only for an `employee`
 * subject — `path` is `null` for the other three, `useApiQuery`'s own "nothing to
 * ask yet" — and a `403`/`404` (an administrator who holds `manage_policies` but no
 * `workforce.view_*` code) falls back to the raw id rather than an error box: this
 * cell's job is to name the subject, and the id already does that honestly.
 */
function SubjectCell({
  grant,
  departmentNameById,
}: {
  readonly grant: PermissionGrant;
  readonly departmentNameById: ReadonlyMap<string, string>;
}): ReactNode {
  const { t } = useI18n();
  const path = grant.subject_type === 'employee' ? employeePath(grant.subject_key) : null;
  const profile = useApiQuery<EmployeeProfile>(path);
  const resolvedName = profile.status === 'ok' ? (profile.data?.full_name ?? null) : null;
  return <>{subjectLabelText(t, grant, departmentNameById, resolvedName)}</>;
}

const FILTER_ALL = '';

export function PermissionGrantsScreen({ nowMs }: PermissionGrantsScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';
  const nowIso = new Date(nowMs ?? Date.now()).toISOString();

  const [reloadToken, setReloadToken] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [wasReplace, setWasReplace] = useState<boolean | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PermissionGrant | null>(null);

  const [subjectTypeFilter, setSubjectTypeFilter] = useState<PermissionSubjectType | ''>(
    FILTER_ALL,
  );
  const [groupFilter, setGroupFilter] = useState<PermissionGroup | ''>(FILTER_ALL);
  const [effectFilter, setEffectFilter] = useState<PermissionEffect | ''>(FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState<GrantStatus | ''>('active');

  const list = useApiQuery<PermissionGrantList>(PERMISSION_GRANTS_PATH, reloadToken);
  const departments = useApiQuery<EmployeeDepartmentFacetList>(employeeDepartmentsPath());
  const create = useApiMutation<PermissionGrantRequest, PermissionGrant>(
    PERMISSION_GRANTS_PATH,
    'POST',
  );
  const revoke = useApiMutation<undefined, PermissionGrant>(PERMISSION_GRANTS_PATH, 'DELETE');

  const rows = list.data?.grants ?? [];

  const departmentNameById = new Map<string, string>();
  for (const facet of departments.data?.departments ?? []) {
    if (facet.department_id !== null) {
      departmentNameById.set(facet.department_id, facet.department_name ?? facet.department_id);
    }
  }

  const filteredRows = rows.filter((grant) => {
    if (subjectTypeFilter !== FILTER_ALL && grant.subject_type !== subjectTypeFilter) return false;
    if (groupFilter !== FILTER_ALL && permissionGroupOf(grant.permission_code) !== groupFilter) {
      return false;
    }
    if (effectFilter !== FILTER_ALL && grant.effect !== effectFilter) return false;
    if (statusFilter !== FILTER_ALL && grantStatusOf(grant, nowIso) !== statusFilter) return false;
    return true;
  });

  const revokeEmployeeId =
    revokeTarget?.subject_type === 'employee' ? revokeTarget.subject_key : null;
  const revokeEmployee = useApiQuery<EmployeeProfile>(
    revokeEmployeeId === null ? null : employeePath(revokeEmployeeId),
  );

  // `submit` resolves rather than rejects (`useApiMutation`'s own docblock), so a
  // `.then()` on it fires on a refused grant exactly as it fires on a success — a
  // spurious full reload on every rejected "Cấp quyền". The same bug `RosterDrawer.
  // tsx`'s `submitAssign` had (Task T2-E-web), fixed the same way here: this effect
  // only fires on the transition **to** `'ok'`, which a refusal never reaches.
  useEffect(() => {
    if (create.status === 'ok') {
      setReloadToken((value) => value + 1);
    }
  }, [create.status]);

  const submitGrant = (body: PermissionGrantRequest): void => {
    const existing = existingGrantFor(
      rows,
      body.subject_type,
      body.subject_key,
      body.permission_code,
    );
    setWasReplace(existing !== null);
    void create.submit(body);
  };

  const openRevoke = (grant: PermissionGrant): void => {
    revoke.reset();
    setRevokeTarget(grant);
  };

  // Same bug, same fix, and here it is worse than a spurious reload: closing the
  // panel on a refusal (the old `.then()`'s unconditional `setRevokeTarget(null)`)
  // would hide the very error `RevokeGrantPanel` exists to show. This effect fires
  // only on the transition **to** `'ok'`, which a refusal never reaches.
  useEffect(() => {
    if (revoke.status === 'ok') {
      setReloadToken((value) => value + 1);
      setRevokeTarget(null);
    }
  }, [revoke.status]);

  const confirmRevoke = (): void => {
    if (revokeTarget === null) return;
    void revoke.submit(undefined, permissionGrantPath(revokeTarget.id));
  };

  return (
    <div className="wp-stack">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.adminPermissions')}</ScreenHeading>
      </div>

      <p className="wp-small wp-muted" data-testid="t2d-intro">
        {t('adminPermissions.intro')}
      </p>

      <div>
        <button
          type="button"
          className="wp-button wp-button--primary"
          data-testid="t2d-open-form"
          onClick={() => {
            create.reset();
            setShowForm(true);
          }}
        >
          {t('adminPermissions.create.open')}
        </button>
      </div>

      {showForm ? (
        <GrantForm
          departments={departments.data?.departments ?? []}
          pending={create.status === 'pending'}
          error={create.status === 'error' ? create.error : null}
          onSubmit={submitGrant}
          onCancel={() => {
            setShowForm(false);
            create.reset();
          }}
        />
      ) : null}

      {create.status === 'ok' && create.data !== null ? (
        <StatusNote
          severity="low"
          title={
            wasReplace === true
              ? t('adminPermissions.form.updated')
              : t('adminPermissions.form.created')
          }
          testId="t2d-form-ok"
        />
      ) : null}

      <fieldset className="wp-filters" data-testid="t2d-filters">
        <legend>{t('adminPermissions.filters.legend')}</legend>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="t2d-filter-subject-type">
            {t('adminPermissions.filter.subjectType')}
          </label>
          <select
            className="wp-field__input"
            id="t2d-filter-subject-type"
            value={subjectTypeFilter}
            data-testid="t2d-filter-subject-type"
            onChange={(event) => {
              setSubjectTypeFilter(event.target.value as PermissionSubjectType | '');
            }}
          >
            <option value={FILTER_ALL}>{t('adminPermissions.filter.subjectType.all')}</option>
            {PERMISSION_SUBJECT_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(SUBJECT_TYPE_LABEL_KEY[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="t2d-filter-group">
            {t('adminPermissions.filter.group')}
          </label>
          <select
            className="wp-field__input"
            id="t2d-filter-group"
            value={groupFilter}
            data-testid="t2d-filter-group"
            onChange={(event) => {
              setGroupFilter(event.target.value as PermissionGroup | '');
            }}
          >
            <option value={FILTER_ALL}>{t('adminPermissions.filter.group.all')}</option>
            {PERMISSION_GROUPS.map((value) => (
              <option key={value} value={value}>
                {t(PERMISSION_GROUP_LABEL_KEY[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="t2d-filter-effect">
            {t('adminPermissions.filter.effect')}
          </label>
          <select
            className="wp-field__input"
            id="t2d-filter-effect"
            value={effectFilter}
            data-testid="t2d-filter-effect"
            onChange={(event) => {
              setEffectFilter(event.target.value as PermissionEffect | '');
            }}
          >
            <option value={FILTER_ALL}>{t('adminPermissions.filter.effect.all')}</option>
            {PERMISSION_EFFECTS.map((value) => (
              <option key={value} value={value}>
                {t(EFFECT_PRESENTATION[value].labelKey)}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-field">
          <label className="wp-field__label" htmlFor="t2d-filter-status">
            {t('adminPermissions.filter.status')}
          </label>
          <select
            className="wp-field__input"
            id="t2d-filter-status"
            value={statusFilter}
            data-testid="t2d-filter-status"
            onChange={(event) => {
              setStatusFilter(event.target.value as GrantStatus | '');
            }}
          >
            {GRANT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(GRANT_STATUS_PRESENTATION[value].labelKey)}
              </option>
            ))}
            <option value={FILTER_ALL}>{t('adminPermissions.filter.status.all')}</option>
          </select>
        </div>
      </fieldset>

      <section className="wp-section" aria-labelledby="t2d-list-heading">
        <h2 className="wp-section__title" id="t2d-list-heading">
          {t('adminPermissions.list.heading')}
        </h2>

        {list.status === 'loading' ? (
          <Skeleton
            rows={5}
            variant="block"
            label={t('adminPermissions.list.loading')}
            testId="t2d-list-skeleton"
          />
        ) : null}

        {list.status === 'error' && list.error !== null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={list.reload}
            subject={t('adminPermissions.subject')}
            testId="t2d-list-error"
          />
        ) : null}

        {list.status === 'ok' && filteredRows.length === 0 ? (
          <EmptyState
            title={t('adminPermissions.list.empty.title')}
            description={t('adminPermissions.list.empty.body')}
            testId="t2d-list-empty"
          />
        ) : null}

        {list.status === 'ok' && filteredRows.length > 0 ? (
          <div className="wp-table-scroll">
            <table className="wp-table wp-table--wide" data-testid="t2d-list">
              <caption className="wp-visually-hidden">{t('adminPermissions.list.caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('adminPermissions.list.col.subject')}</th>
                  <th scope="col">{t('adminPermissions.list.col.code')}</th>
                  <th scope="col">{t('adminPermissions.list.col.effect')}</th>
                  <th scope="col">{t('adminPermissions.list.col.reason')}</th>
                  <th scope="col">{t('adminPermissions.list.col.grantedBy')}</th>
                  <th scope="col">{t('adminPermissions.list.col.expiresAt')}</th>
                  <th scope="col">{t('adminPermissions.list.col.status')}</th>
                  <th scope="col">
                    <span className="wp-visually-hidden">
                      {t('adminPermissions.list.col.actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((grant) => {
                  const status = grantStatusOf(grant, nowIso);
                  const group = permissionGroupOf(grant.permission_code);
                  return (
                    <tr key={grant.id} data-testid={`t2d-row-${grant.id}`}>
                      <td data-testid={`t2d-subject-${grant.id}`}>
                        <SubjectCell grant={grant} departmentNameById={departmentNameById} />
                      </td>
                      <td data-testid={`t2d-code-${grant.id}`}>
                        {t('adminPermissions.codeCell', {
                          group: t(PERMISSION_GROUP_LABEL_KEY[group]),
                          code: grant.permission_code,
                        })}
                      </td>
                      <td data-testid={`t2d-effect-${grant.id}`}>
                        <Badge
                          severity={EFFECT_PRESENTATION[grant.effect].severity}
                          icon={EFFECT_PRESENTATION[grant.effect].icon}
                          label={t(EFFECT_PRESENTATION[grant.effect].labelKey)}
                        />
                      </td>
                      <td data-testid={`t2d-reason-${grant.id}`}>
                        {grant.reason ?? t('adminPermissions.noReason')}
                      </td>
                      <td data-testid={`t2d-granted-${grant.id}`}>
                        {t('adminPermissions.grantedCell', {
                          by: grant.granted_by ?? t('adminPermissions.grantedByUnknown'),
                          at: formatInstant(grant.granted_at, locale, timezone),
                        })}
                      </td>
                      <td data-testid={`t2d-expires-${grant.id}`}>
                        {grant.expires_at === null
                          ? t('adminPermissions.noExpiry')
                          : formatInstant(grant.expires_at, locale, timezone)}
                      </td>
                      <td data-testid={`t2d-status-${grant.id}`}>
                        <Badge
                          severity={GRANT_STATUS_PRESENTATION[status].severity}
                          icon={GRANT_STATUS_PRESENTATION[status].icon}
                          label={t(GRANT_STATUS_PRESENTATION[status].labelKey)}
                        />
                      </td>
                      <td>
                        {grant.revoked_at === null ? (
                          <button
                            type="button"
                            className="wp-button wp-button--quiet"
                            data-testid={`t2d-revoke-open-${grant.id}`}
                            onClick={() => {
                              openRevoke(grant);
                            }}
                          >
                            {t('adminPermissions.revoke.action')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {revokeTarget === null ? null : (
        <RevokeGrantPanel
          grant={revokeTarget}
          subjectLabel={subjectLabelText(
            t,
            revokeTarget,
            departmentNameById,
            revokeEmployee.status === 'ok' ? (revokeEmployee.data?.full_name ?? null) : null,
          )}
          pending={revoke.status === 'pending'}
          error={revoke.status === 'error' ? revoke.error : null}
          onConfirm={confirmRevoke}
          onCancel={() => {
            setRevokeTarget(null);
            revoke.reset();
          }}
        />
      )}

      {revoke.status === 'ok' && revoke.data !== null ? (
        <StatusNote
          severity="low"
          title={t('adminPermissions.revoke.done')}
          testId="t2d-revoke-done"
        />
      ) : null}
    </div>
  );
}
